import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FormControl, Stack, TextField, Select, MenuItem, InputLabel, Button, Box, Chip, Typography
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { Container, DataGrid } from '@/components/common';
import { useCourseList } from '@/hooks/courses.hook';
import { formatDate } from '@/util/dateUtils';
import CourseCategoryCell from '../../components/CourseCategoryCell';
import CourseLanguageCell from '../../components/CourseLanguageCell';
import { LANGUAGE_OPTIONS } from '@/constants/courses';
import { LIST_VIEW_SESSION_KEYS } from '@/util/listViewSessionKeys';
import { useListViewSessionPersistence } from '@/util/useListViewSessionPersistence';

const COURSES_DEFAULT_PAGINATION = { page: 0, pageSize: 10 };
const COURSES_DEFAULT_FILTERS = {
  freeYn: null,
  languageId: null,
  courseType: null,
  deleted: false,
};

/**
 * 과정(Course) 관리 페이지 컴포넌트
 * 
 * 교육 과정 목록을 조회하고 관리하는 페이지입니다.
 * 
 * 데이터 구조:
 * - courseId (int) 과정ID
 * - courseName (string) 과정명
 * - courseCategory (string) 과정 카테고리
 * - courseCount (int) 과정 수
 * - courseObj (string) 과정 목표
 * - instId (int) 강사ID
 * - instName (string) 강사명
 * - freeYn (boolean) 무료 여부
 * - hasContent (boolean) 콘텐츠 보유 여부
 * - hasTraining (boolean) 훈련 보유 여부
 * - courseRegDate (datetime) 등록일
 */
const CoursesPage = () => {
  const navigate = useNavigate();

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetListSession,
  } = useListViewSessionPersistence(
    LIST_VIEW_SESSION_KEYS.courses,
    COURSES_DEFAULT_PAGINATION,
    COURSES_DEFAULT_FILTERS
  );

  const freeYn = filters.freeYn ?? null;
  const languageId = filters.languageId ?? null;
  const courseType = filters.courseType ?? null;
  const deleted = filters.deleted ?? false;

  const setFreeYn = useCallback((v) => {
    setFilters((f) => ({ ...f, freeYn: typeof v === 'function' ? v(f.freeYn) : v }));
  }, [setFilters]);
  const setLanguageId = useCallback((v) => {
    setFilters((f) => ({ ...f, languageId: typeof v === 'function' ? v(f.languageId) : v }));
  }, [setFilters]);
  const setCourseType = useCallback((v) => {
    setFilters((f) => ({ ...f, courseType: typeof v === 'function' ? v(f.courseType) : v }));
  }, [setFilters]);
  const setDeleted = useCallback((v) => {
    setFilters((f) => ({ ...f, deleted: typeof v === 'function' ? v(f.deleted) : v }));
  }, [setFilters]);

  // 백엔드 API 파라미터 구성
  const queryParams = {
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
  };
  
  // undefined가 아닌 값만 추가
  if (freeYn !== null) {
    queryParams.freeYn = freeYn;
  }
  if (languageId !== null) {
    queryParams.languageId = languageId;
  }
  if (courseType !== null) {
    queryParams.courseType = courseType;
  }
  if (deleted) {
    queryParams.deleted = true;
  }

  const { data, isLoading } = useCourseList(queryParams);

  const total = data?.total ?? 0;
  const rowData = data?.list ?? [];

  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    if (!isLoading) setRowCountState(total);
  }, [total, isLoading]);

  /**
   * 필터 초기화 핸들러
   */
  const handleReset = useCallback(() => {
    resetListSession();
  }, [resetListSession]);

  /**
   * 행 클릭 핸들러
   */
  const handleRowClick = useCallback((params) => {
    navigate(`/education/courses/detail/${params.row.courseId}`);
  }, [navigate]);

  // 컬럼 정의 (메모이제이션하여 불필요한 리렌더링 방지)
  const columns = useMemo(() => [
    {
      field: 'courseId',
      headerName: '과정ID',
      flex: 1,
      sortable: false,
      minWidth: 100,
      type: 'number',
    },
    {
      field: 'courseName',
      headerName: '과정명',
      flex: 1,
      sortable: false,
      minWidth: 200,
    },
    {
      field: 'courseCategory',
      headerName: '카테고리',
      flex: 1,
      sortable: false,
      minWidth: 120,
      renderCell: (params) => <CourseCategoryCell row={params.row} />,
    },
    {
      field: 'freeYn',
      headerName: '무료 여부',
      flex: 1,
      sortable: false,
      minWidth: 100,
      renderCell: (params) => {
        const label = params.row.freeYn ? '무료' : '유료';
        const color = params.row.freeYn ? 'success' : 'default';
        return (
          <Chip
            label={label}
            size="small"
            color={color}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'languageName',
      headerName: '언어',
      flex: 1,
      sortable: false,
      minWidth: 100,
      renderCell: (params) => {
        return <CourseLanguageCell row={params.row} />;
      },
    },
    {
      field: 'hasContent',
      headerName: '콘텐츠 공개 여부',
      flex: 1,
      sortable: false,
      minWidth: 100,
      renderCell: (params) => {
        const label = params.row.hasContent ? '공개됨' : '미공개';
        const color = params.row.hasContent ? 'success' : 'error';
        return (
          <Chip
            label={label}
            size="small"
            color={color}
            variant="outlined"
          />
        );
      },
    },
    // {
    //   field: 'hasTraining',
    //   headerName: '훈련 보유',
    //   flex: 1,
    //   sortable: false,
    //   minWidth: 100,
    //   renderCell: (params) => {
    //     const label = params.row.hasTraining ? '보유' : '미보유';
    //     const color = params.row.hasTraining ? 'success' : 'error';
    //     return (
    //       <Chip
    //         label={label}
    //         size="small"
    //         color={color}
    //         variant="outlined"
    //       />
    //     );
    //   },
    // },
    {
      field: 'courseRegDate',
      headerName: '등록일',
      flex: 1,
      sortable: false,
      minWidth: 120,
      renderCell: (params) => {
        return formatDate(params.row.courseRegDate) || '-';
      },
    },
    ...(deleted ? [{
      field: 'deletedAt',
      headerName: '폐강일',
      flex: 1,
      sortable: false,
      minWidth: 160,
      renderCell: (params) => {
        const when = formatDate(params.row.deletedAt) || '-';
        const who = params.row.deletedBy ? ` (${params.row.deletedBy})` : '';
        return `${when}${who}`;
      },
    }] : []),
  ], [deleted]);

  return (
    <Container title='강의 관리'>
      {/* 1. 상단 필터 및 버튼 영역 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        {/* 필터 그룹 (백엔드 API에 맞춤: freeYn, languageId, courseType) */}
        <Stack direction="row" gap={1} alignItems="center">
          <FormControl size='small' sx={{ width: 150 }}>
            <InputLabel>무료 여부</InputLabel>
            <Select
              value={freeYn === null ? 'all' : freeYn ? 'free' : 'paid'}
              label="무료 여부"
              onChange={(e) => {
                const value = e.target.value;
                setFreeYn(value === 'all' ? null : value === 'free');
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value='all'>전체</MenuItem>
              <MenuItem value='free'>무료</MenuItem>
              <MenuItem value='paid'>유료</MenuItem>
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ width: 150 }}>
            <InputLabel>언어</InputLabel>
            <Select
              value={languageId === null ? 'all' : languageId}
              label="언어"
              onChange={(e) => {
                const value = e.target.value;
                setLanguageId(value === 'all' ? null : parseInt(value));
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value='all'>전체</MenuItem>
              {LANGUAGE_OPTIONS.map((lang) => (
                <MenuItem key={lang.languageId} value={lang.languageId}>
                  {lang.languageName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ width: 200 }}>
            <InputLabel>카테고리</InputLabel>
            <Select
              value={courseType || 'all'}
              label="카테고리"
              onChange={(e) => {
                const value = e.target.value;
                setCourseType(value === 'all' ? null : value);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value='all'>전체</MenuItem>
              <MenuItem value='SOFTWARE_SECURITY'>소프트웨어 개발보안 교육</MenuItem>
              <MenuItem value='INFORMATION_SECURITY'>정보보안 교육</MenuItem>
              <MenuItem value='PROGRAMMING'>프로그래밍 교육</MenuItem>
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ width: 150 }}>
            <InputLabel>상태</InputLabel>
            <Select
              value={deleted ? 'closed' : 'active'}
              label="상태"
              onChange={(e) => {
                setDeleted(e.target.value === 'closed');
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
            >
              <MenuItem value='active'>운영 중</MenuItem>
              <MenuItem value='closed'>폐강</MenuItem>
            </Select>
          </FormControl>

          <Button variant='outlined' onClick={handleReset} sx={{ height: 40 }}>초기화</Button>
        </Stack>

        {/* 우측 액션 버튼 그룹 */}
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/education/courses/new')}
          >
            과정 등록
          </Button>
        </Stack>
      </Stack>

      {/* 2. 데이터 그리드 */}
      <DataGrid
        rows={rowData}
        columns={columns}
        loading={isLoading}
        rowCount={rowCountState}
        height={'auto'}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row) => row.courseId}
        onRowClick={handleRowClick}
      />
    </Container>
  );
};

export default CoursesPage;
